import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import { connect } from 'react-redux';
import classnames from 'classnames';
import get from 'lodash.get';

// components
import { FormattedMessage } from 'react-intl';
import { Button } from 'dibs-elements/exports/Button';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { SharedContactDealerWrapper } from '../../contactDealer/SharedContactDealerWrapper';
import { TradeOnboardingCoachmarkConditionalLoader } from '../../global/tradeOnboarding/TradeOnboardingCoachmarkConditionalLoader';
import { NOTIFICATION_KEYS } from '../PdpSharedNotifications/pdpSharedNotificationConstants';
import { startConversationMessage } from '../pdpMessages';
import { PdpRespTradeOnApprovalCoachmarkLoader } from '../PdpRespTradeOnApprovalCoachmark/PdpRespTradeOnApprovalCoachmarkLoader';
// styles
import styles from './PdpSharedButtons.scss';

export const PdpSharedContactDealerButtonComponent = ({
    fullWidth,
    type,
    launchOrigin,
    item,
    size,
    buttonType,
    source,
    user = {},
    showPdpTradeOnboardingModal,
}) => {
    const buttonClassName = classnames(styles.requestInfoButton, { [styles.fullWidth]: fullWidth });

    const contactTheSellerMessage = (
        <FormattedMessage
            id="PdpSharedContactDealerButton.contactTheSeller"
            defaultMessage="Contact Seller"
        />
    );

    const requestCustomizationMessage = (
        <FormattedMessage
            id="PdpSharedContactDealerButton.requestCustomization"
            defaultMessage="Request More Info"
        />
    );

    const requestPriceMessage = (
        <FormattedMessage
            id="PdpSharedContactDealerButton.requestPrice"
            defaultMessage="Request Price"
        />
    );

    const requestMoreImages = (
        <FormattedMessage
            id="PdpSharedContactDealerButton.requestMoreImages"
            defaultMessage="Request More Images"
        />
    );

    let modalType = 'CONTACT_DEALER';
    let message;
    let coachmark;

    switch (type) {
        case 'REQUEST_PRICE':
            message = requestPriceMessage;
            launchOrigin = 'request price button';
            modalType = type;
            break;
        case 'REQUEST_CUSTOMIZATION':
            message = requestCustomizationMessage;
            break;
        case 'REQUEST_IMAGES':
            message = requestMoreImages;
            modalType = type;
            break;
        default:
            message =
                source === 'shipment-info-link'
                    ? contactTheSellerMessage
                    : startConversationMessage;
            coachmark = (
                <Fragment>
                    <TradeOnboardingCoachmarkConditionalLoader
                        isPdp
                        item={item}
                        user={user}
                        showModal={showPdpTradeOnboardingModal}
                    />
                    <PdpRespTradeOnApprovalCoachmarkLoader item={item} user={user} />
                </Fragment>
            );
            break;
    }

    return (
        <CurrencyProvider
            render={({ currency }) => (
                <SharedContactDealerWrapper
                    item={item}
                    launchOrigin={launchOrigin}
                    modalType={modalType}
                    user={user}
                    currency={currency}
                >
                    {source === 'shipment-info-link' ? (
                        <a
                            className={styles.contactSellerLink}
                            dataTn="item-actions-request-information"
                        >
                            {message}
                        </a>
                    ) : (
                        <div className={styles.buttonWrapper}>
                            {coachmark}
                            <Button
                                type={buttonType}
                                className={buttonClassName}
                                dataTn="item-actions-request-information"
                                size={size}
                            >
                                {message}
                            </Button>
                        </div>
                    )}
                </SharedContactDealerWrapper>
            )}
        />
    );
};

PdpSharedContactDealerButtonComponent.propTypes = {
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    item: PropTypes.object.isRequired,
    user: PropTypes.object,
    source: PropTypes.string,
    type: PropTypes.oneOf([
        'CONTACT_DEALER',
        'REQUEST_CUSTOMIZATION',
        'REQUEST_PRICE',
        'REQUEST_IMAGES',
    ]),
    fullWidth: PropTypes.bool,
    launchOrigin: PropTypes.string,
    buttonType: PropTypes.oneOf(['primary', 'secondary', 'textCta']),
    showPdpTradeOnboardingModal: PropTypes.bool.isRequired,
};

PdpSharedContactDealerButtonComponent.defaultProps = {
    size: 'large',
    buttonType: 'primary',
    type: 'CONTACT_DEALER',
    launchOrigin: 'contact link in pdp - sidebar',
    fullWidth: true,
};

function mapStateToProps(state) {
    const notifications = get(state, 'sharedNotificationsReducer.notifications') || {};
    return {
        showPdpTradeOnboardingModal:
            notifications[NOTIFICATION_KEYS.PDP_TRADE_ONBOARDING_MODAL] || false,
    };
}

export const PdpSharedContactDealerButton = createFragmentContainer(
    connect(mapStateToProps)(PdpSharedContactDealerButtonComponent),
    {
        item: graphql`
            fragment PdpSharedContactDealerButton_item on Item {
                ...SharedContactDealerWrapper_item
                ...TradeOnboardingCoachmarkConditionalLoader_item
                ...PdpRespTradeOnApprovalCoachmarkLoader_item
            }
        `,
        user: graphql`
            fragment PdpSharedContactDealerButton_user on User {
                ...SharedContactDealerWrapper_user
                ...TradeOnboardingCoachmarkConditionalLoader_user
                ...PdpRespTradeOnApprovalCoachmarkLoader_user
            }
        `,
    }
);
